import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchJobTypes,
  createJobType,
  updateJobType,
  deleteJobType,
  fetchJobTypeChecklist,
  saveJobTypeChecklist,
} from "../api/checklists";

// One shared empty array for the loading state. A fresh `[]` default is a new
// identity every render, which re-fires any effect/memo keyed on it — that is
// what looped the dashboard charts (UI audit, Oct 12 2026).
const EMPTY = [];

// The job type list — the source for JobTypeField's dropdown and for the
// Settings templates section. Job types changed from a hardcoded frontend list
// to DB records when checklist templates landed.
export function useJobTypes({ includeInactive = false } = {}) {
  const queryClient = useQueryClient();
  const params = includeInactive ? { include_inactive: true } : {};

  const { data: jobTypes = EMPTY, isLoading } = useQuery({
    queryKey: ["jobTypes", { includeInactive }],
    queryFn: () => fetchJobTypes(params),
    staleTime: 300_000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["jobTypes"] });

  const createMutation = useMutation({ mutationFn: createJobType, onSuccess: invalidate });
  const updateMutation = useMutation({
    mutationFn: ({ id, ...payload }) => updateJobType(id, payload),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({ mutationFn: deleteJobType, onSuccess: invalidate });

  return {
    jobTypes,
    isLoading,
    createJobType: createMutation.mutateAsync,
    updateJobType: updateMutation.mutateAsync,
    deleteJobType: deleteMutation.mutateAsync,
    saving:
      createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
}

// One job type's template. `enabled: false` while nothing is selected.
export function useJobTypeChecklist(jobTypeId) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["jobTypes", jobTypeId, "checklist"],
    queryFn: () => fetchJobTypeChecklist(jobTypeId),
    enabled: Boolean(jobTypeId),
  });

  const saveMutation = useMutation({
    mutationFn: (payload) => saveJobTypeChecklist(jobTypeId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["jobTypes"] }),
  });

  return {
    checklist: data,
    isLoading,
    saveChecklist: saveMutation.mutateAsync,
    saving: saveMutation.isPending,
  };
}
